import { ArrowUpRight, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const percent = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "Unavailable";
  const number = Number(value);
  return `${(number * 100).toFixed(number >= 0.01 ? 1 : 2)}%`;
};

const compact = (value) => value === null || value === undefined ? "Unavailable" : new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(Number(value));

/**
 * A same-page table for queries close to page one.
 *
 * This intentionally stays separate from the main finding feed. These rows are
 * practical quick wins, not full diagnosis cards.
 */
export default function QuickWinsTable({ rows = [], loading = false }) {
  if (loading) {
    return (
      <section className="rounded-card border border-line bg-white p-5" data-testid="quick-wins-loading">
        <div className="h-5 w-48 rounded bg-slate-100" />
        <div className="mt-4 space-y-3">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-12 rounded bg-slate-100" />
          ))}
        </div>
      </section>
    );
  }

  if (!rows.length) {
    return (
      <section className="rounded-card border border-line bg-white p-5" data-testid="quick-wins-empty">
        <div className="flex items-start gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-compact bg-emerald-50 text-emerald-700">
            <Search size={17} aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-heading text-h3 text-navy-950">Pages close to page one</h2>
            <p className="mt-1 text-small text-slate-600">
              No position 11 to 20 opportunities showed up for this date range.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-card border border-line bg-white" data-testid="quick-wins-table">
      <div className="border-b border-line p-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="font-heading text-h3 text-navy-950">Pages close to page one</h2>
            <p className="mt-1 max-w-[68ch] text-small text-slate-600">
              These queries already rank in positions 11 to 20. SEO Signals sorts them by impressions and position, so you see the pages with the clearest chance to move onto page one first.
            </p>
          </div>
          <Badge variant="opportunity">Quick wins</Badge>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="ss-report-table min-w-full divide-y divide-line text-left text-small">
          <thead className="bg-offwhite text-xs uppercase tracking-[0.12em] text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Query</th>
              <th className="px-5 py-3 font-semibold">Page</th>
              <th className="ss-report-number px-5 py-3 text-right font-semibold">Position</th>
              <th className="ss-report-number px-5 py-3 text-right font-semibold">Impressions</th>
              <th className="ss-report-number px-5 py-3 text-right font-semibold">CTR</th>
              <th className="px-5 py-3 font-semibold">Next action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line bg-white">
            {rows.map((row) => (
              <tr key={`${row.query}-${row.page}`}>
                <td className="max-w-[260px] px-5 py-4 font-semibold text-navy-950">{row.query}</td>
                <td className="ss-report-text max-w-[320px] px-5 py-4 text-slate-600">
                  <a className="ss-url-link inline-flex items-start gap-1 hover:text-emerald-700 hover:underline" href={row.page} target="_blank" rel="noreferrer">
                    <span>{row.page}</span>
                    <ArrowUpRight size={13} aria-hidden="true" />
                  </a>
                </td>
                <td className="ss-report-number px-5 py-4 text-right tabular text-navy-950">{Number(row.position || 0).toFixed(1)}</td>
                <td className="ss-report-number px-5 py-4 text-right tabular text-navy-950">{compact(row.impressions)}</td>
                <td className="ss-report-number px-5 py-4 text-right tabular text-navy-950">{percent(row.ctr)}</td>
                <td className="max-w-[300px] px-5 py-4 text-slate-600">{row.action}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
